import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Phone,
  Navigation,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Package,
  FileText,
  User,
  Truck,
  Home,
  Clock,
  Map,
  Play,
  ArrowLeft,
  List,
  X,
  Euro,
  AlertTriangle,
  MessageSquare,
  Download,
  Send,
  Eraser,
  RotateCcw,
  RefreshCw,
  Link2,
  Camera,
  LogOut,
  QrCode,
  Receipt,
  Fuel,
  Repeat,
  Star,
  CreditCard,
  Globe,
  Mail,
  Building2,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

// Lien d'avis Google (fourni par Sanchelec)
const GOOGLE_REVIEW_URL = "https://g.page/r/CVNwYH14BsfdEAE/review";

// Construit un QR de virement SEPA (norme EPC069-12) — scannable par les apps bancaires
const buildEpcPayload = (name, iban, bic) => {
  const cleanIban = (iban || "").replace(/\s+/g, "").toUpperCase();
  if (!cleanIban) return "";
  // Montant et communication volontairement vides (le client/livreur les renseigne)
  return ["BCD", "002", "1", "SCT", (bic || "").trim(), (name || "Sanchelec").trim(), cleanIban, "", "", "", ""].join("\n");
};

// ---- Modal QR à onglets (Site web / Avis Google / Paiement) ----
const QrTabsModal = ({ open, onClose, user, params }) => {
  const [tab, setTab] = useState("site");
  if (!open) return null;

  const siteUrl = `https://www.top-cocoon.be?utm_source=livreur&utm_medium=qr&utm_campaign=livraison${user?.name ? `&ref=${encodeURIComponent(user.name.replace(/\s+/g, "_"))}` : ""}`;
  const ibanRaw = params?.facture_iban || "";
  const beneficiaire = params?.facture_company_name || "Sanchelec";
  const bic = params?.facture_bic || "";
  const epc = buildEpcPayload(beneficiaire, ibanRaw, bic);
  const ibanFmt = ibanRaw.replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();

  const tabs = [
    { id: "site", label: "Site web", icon: Globe },
    { id: "avis", label: "Avis Google", icon: Star },
    { id: "paiement", label: "Paiement", icon: CreditCard },
  ];

  const downloadQr = (filename) => {
    const canvas = document.getElementById("qr-active-canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = filename;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="qr-modal">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-slate-100" data-testid="qr-close-btn">
          <X className="w-5 h-5 text-slate-500" />
        </button>

        {/* Onglets */}
        <div className="flex gap-1 mb-4 bg-slate-100 rounded-xl p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg text-xs font-medium transition-colors ${tab === t.id ? "bg-white shadow text-indigo-600" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`qr-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "site" && (
          <>
            <h2 className="text-lg font-bold text-slate-800 mb-1">Découvrez TopCocoon</h2>
            <p className="text-xs text-slate-500 mb-4">Scannez pour visiter notre site</p>
            <div className="bg-white p-3 rounded-xl inline-block shadow-inner border border-slate-200">
              <QRCodeCanvas id="qr-active-canvas" value={siteUrl} size={230} level="H" imageSettings={{ src: "/logo-topcocoon.png", height: 46, width: 46, excavate: true }} />
            </div>
            <p className="text-[11px] font-mono text-slate-400 mt-3 break-all">www.top-cocoon.be</p>
            <Button onClick={() => downloadQr(`qr-site${user?.name ? "-" + user.name.replace(/\s+/g, "-") : ""}.png`)} variant="outline" className="w-full mt-4" data-testid="qr-download-btn">
              <Download className="w-4 h-4 mr-2" />Télécharger
            </Button>
          </>
        )}

        {tab === "avis" && (
          <>
            <h2 className="text-lg font-bold text-slate-800 mb-1">Laissez-nous un avis ⭐</h2>
            <p className="text-xs text-slate-500 mb-4">Scannez pour noter Sanchelec sur Google</p>
            <div className="bg-white p-3 rounded-xl inline-block shadow-inner border border-slate-200">
              <QRCodeCanvas id="qr-active-canvas" value={GOOGLE_REVIEW_URL} size={230} level="H" />
            </div>
            <p className="text-[11px] text-amber-600 mt-3">Merci pour votre soutien 🙏</p>
            <Button onClick={() => downloadQr("qr-avis-google.png")} variant="outline" className="w-full mt-4" data-testid="qr-download-btn">
              <Download className="w-4 h-4 mr-2" />Télécharger
            </Button>
          </>
        )}

        {tab === "paiement" && (
          <>
            <h2 className="text-lg font-bold text-slate-800 mb-1">Payer par virement</h2>
            <p className="text-xs text-slate-500 mb-4">Scannez avec votre app bancaire — le virement se pré-remplit</p>
            {epc ? (
              <>
                <div className="bg-white p-3 rounded-xl inline-block shadow-inner border border-slate-200">
                  <QRCodeCanvas id="qr-active-canvas" value={epc} size={230} level="M" />
                </div>
                <div className="mt-3 text-left bg-slate-50 rounded-lg p-3 space-y-1">
                  <p className="text-xs text-slate-500">Bénéficiaire</p>
                  <p className="text-sm font-semibold text-slate-800" data-testid="qr-pay-beneficiaire">{beneficiaire}</p>
                  <p className="text-xs text-slate-500 mt-1">IBAN</p>
                  <p className="text-sm font-mono text-slate-800 break-all" data-testid="qr-pay-iban">{ibanFmt}</p>
                  {bic && <><p className="text-xs text-slate-500 mt-1">BIC</p><p className="text-sm font-mono text-slate-800">{bic}</p></>}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">Montant et communication à indiquer par le client.</p>
                <Button onClick={() => downloadQr("qr-paiement-sanchelec.png")} variant="outline" className="w-full mt-3" data-testid="qr-download-btn">
                  <Download className="w-4 h-4 mr-2" />Télécharger
                </Button>
              </>
            ) : (
              <p className="text-sm text-orange-600 py-8" data-testid="qr-pay-no-iban">
                Aucun IBAN configuré. Renseigne l'IBAN de Sanchelec dans Paramètres → Facturation.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ---- Signature Canvas Component ----
const SignatureCanvas = ({ onSignatureChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1e293b";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    setHasSignature(true);
  };

  const endDraw = () => {
    setIsDrawing(false);
    if (hasSignature && canvasRef.current) {
      onSignatureChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange(null);
  };

  return (
    <div>
      <div className="border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-white" data-testid="signature-canvas-container">
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          className="w-full touch-none h-[110px] sm:h-[150px]"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-slate-400">{hasSignature ? "Signature enregistree" : "Signez avec le doigt ou la souris"}</p>
        {hasSignature && (
          <Button size="sm" variant="ghost" onClick={clear} className="text-slate-500 h-7" data-testid="clear-signature-btn">
            <Eraser className="w-3.5 h-3.5 mr-1" />Effacer
          </Button>
        )}
      </div>
    </div>
  );
};

// ---- Main Component ----
const SOURCES_ACQUISITION = ["Facebook", "Instagram", "TikTok", "Google", "Bouche-à-oreille", "Ancien client", "Autre"];

const Livreur = () => {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const isLivreur = (user?.role || "admin") === "livreur";
  const [tournees, setTournees] = useState([]);
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedTournee, setSelectedTournee] = useState(null);
  const [tourneeDetails, setTourneeDetails] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showStopsList, setShowStopsList] = useState(false);
  const [tourneeStatut, setTourneeStatut] = useState(null);

  // Encaissement state
  const [montantEncaisse, setMontantEncaisse] = useState("");
  const [noteEncaissement, setNoteEncaissement] = useState("");
  const [virementBeneficiaire, setVirementBeneficiaire] = useState("cocoon"); // "cocoon" | "autre"
  const [virementLivreurNom, setVirementLivreurNom] = useState("");

  // Delivery confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [photoLivraison, setPhotoLivraison] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // Post-delivery receipt
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [lastDeliveredId, setLastDeliveredId] = useState(null);

  // Report dialog
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportNote, setReportNote] = useState("");
  const [reportCreerProchaine, setReportCreerProchaine] = useState(true);
  const [reportDateSouhaitee, setReportDateSouhaitee] = useState("");
  const [reporting, setReporting] = useState(false);

  // Frais dialog (essence/adblue/péage) — saisie terrain par le livreur
  const [fraisDialogOpen, setFraisDialogOpen] = useState(false);
  const [fraisType, setFraisType] = useState("essence");
  const [fraisMontant, setFraisMontant] = useState("");
  const [fraisNote, setFraisNote] = useState("");
  const [fraisSubmitting, setFraisSubmitting] = useState(false);

  // Exchange dialog
  const [exchangeDialogOpen, setExchangeDialogOpen] = useState(false);
  const [exchangeRapportees, setExchangeRapportees] = useState([]); // [{produit_id, sku, modele, couleur, quantite}]
  const [exchangeReintegrer, setExchangeReintegrer] = useState(true);
  const [exchangeFraisLivraison, setExchangeFraisLivraison] = useState("25");
  const [exchangeDifference, setExchangeDifference] = useState("0");
  const [exchangeRaison, setExchangeRaison] = useState("");
  const [exchangeSubmitting, setExchangeSubmitting] = useState(false);
  // Modifier les articles (substitution sur place)
  const [modifyDialogOpen, setModifyDialogOpen] = useState(false);
  const [modifyItems, setModifyItems] = useState([]);
  const [modifySubmitting, setModifySubmitting] = useState(false);
  const [produits, setProduits] = useState([]);
  const [params, setParams] = useState(null);

  // Payment mode picked when clicking LIVRÉ — set BEFORE opening confirm dialog
  const [paymentMode, setPaymentMode] = useState("liquide");
  const [sourceAcquisition, setSourceAcquisition] = useState("");

  // Km popup au démarrage tournée
  const [kmDialogOpen, setKmDialogOpen] = useState(false);
  const [vehicules, setVehicules] = useState([]);
  const [kmForm, setKmForm] = useState({ vehicule_id: "", km_depart: "", photo_depart: "", conducteur: "" });
  const [kmSubmitting, setKmSubmitting] = useState(false);

  // Fin de tournée popup
  const [finTourneeOpen, setFinTourneeOpen] = useState(false);
  const [finTourneeForm, setFinTourneeForm] = useState({ km_retour: "", photo_retour: "", remarques: "" });
  const [finTourneeSubmitting, setFinTourneeSubmitting] = useState(false);
  const [openTripInfo, setOpenTripInfo] = useState(null); // { vehicule_id, trip_id, km_depart }

  // Arrivée GPS
  const [arriving, setArriving] = useState(false);
  // Auto-envoi reçu email (idempotent) après confirmation livraison
  const [autoReceiptEmail, setAutoReceiptEmail] = useState(true);
  const [resendingReceipt, setResendingReceipt] = useState(false);
  // Confirmation mobile — extras optionnels repliés par défaut
  const [showConfirmExtras, setShowConfirmExtras] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTournees = async () => {
    try {
      const response = await axios.get(`${API}/tournees`, { headers });
      const activeTournees = response.data.filter(
        (t) => t.statut === "planifiee" || t.statut === "en_cours"
      );
      setTournees(activeTournees);
      if (activeTournees.length > 0 && !selectedTournee) {
        setSelectedTournee(activeTournees[0].id);
        setTourneeStatut(activeTournees[0].statut);
      }
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchTourneeDetails = async (tourneeId) => {
    try {
      const response = await axios.get(`${API}/tournees/${tourneeId}/livraisons`, { headers });
      setTourneeDetails(response.data);
      const firstPending = response.data.livraisons.findIndex((l) => l.statut !== "livree");
      setCurrentIndex(firstPending >= 0 ? firstPending : 0);
    } catch (error) {
      toast.error("Erreur de chargement de la tournee");
    }
  };

  useEffect(() => { fetchTournees(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(`${API}/parametres`, { headers })
      .then((r) => setParams(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Swap PWA manifest + theme color to green "Livreur" variant while on this page
  useEffect(() => {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevManifest = manifestLink?.getAttribute("href");
    const prevTheme = themeMeta?.getAttribute("content");
    if (manifestLink) manifestLink.setAttribute("href", "/manifest-livreur.json");
    if (themeMeta) themeMeta.setAttribute("content", "#10B981");
    document.title = "TopCocoon Livreur";
    return () => {
      if (manifestLink && prevManifest) manifestLink.setAttribute("href", prevManifest);
      if (themeMeta && prevTheme) themeMeta.setAttribute("content", prevTheme);
      document.title = "TopCocoon";
    };
  }, []);

  useEffect(() => {
    if (selectedTournee) {
      fetchTourneeDetails(selectedTournee);
      const t = tournees.find((t) => t.id === selectedTournee);
      if (t) setTourneeStatut(t.statut);
    }
  }, [selectedTournee]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const liv = tourneeDetails?.livraisons[currentIndex];
    if (liv) {
      setMontantEncaisse(liv.montant_encaisse != null ? String(liv.montant_encaisse) : (liv.prix != null ? String(liv.prix) : ""));
      setNoteEncaissement(liv.note_encaissement || "");
    }
  }, [currentIndex, tourneeDetails]);

  const currentLivraison = tourneeDetails?.livraisons[currentIndex];
  const totalStops = tourneeDetails?.livraisons.length || 0;
  const deliveredCount = tourneeDetails?.livraisons.filter((l) => l.statut === "livree").length || 0;

  const goToPrevious = () => { if (currentIndex > 0) setCurrentIndex(currentIndex - 1); };
  const goToNext = () => { if (currentIndex < totalStops - 1) setCurrentIndex(currentIndex + 1); };

  // Open confirmation dialog (instead of direct mark)
  const handleLivreClick = () => {
    setSignatureData(null);
    setPhotoLivraison(null);
    setSourceAcquisition(currentLivraison?.source_acquisition || "");
    setShowConfirmExtras(false);
    setConfirmDialogOpen(true);
  };

  // Confirm delivery with signature
  const handleConfirmDelivery = async () => {
    if (!currentLivraison) return;
    setConfirming(true);
    try {
      const update = {
        statut: "livree",
        montant_encaisse: montantEncaisse ? parseFloat(montantEncaisse) : null,
        mode_paiement: paymentMode,
        note_encaissement: noteEncaissement || "",
        signature: signatureData || null,
        photo_livraison: photoLivraison || null,
        source_acquisition: sourceAcquisition || "",
        virement_beneficiaire: paymentMode === "virement" ? (virementBeneficiaire === "autre" ? `livreur:${virementLivreurNom || "Non précisé"}` : "cocoon") : "",
      };
      await axios.put(`${API}/livraisons/${currentLivraison.id}`, update, { headers });
      toast.success("Livraison confirmee !");
      setLastDeliveredId(currentLivraison.id);
      setConfirmDialogOpen(false);
      setReceiptDialogOpen(true);
      await fetchTourneeDetails(selectedTournee);
      // Auto envoi reçu email (idempotent côté serveur) si activé et email dispo
      if (autoReceiptEmail && currentLivraison.client_email) {
        triggerAutoReceiptEmail(currentLivraison.id);
      }
    } catch (error) {
      toast.error("Erreur");
    } finally {
      setConfirming(false);
    }
  };

  // ===== GPS Arrivée =====
  const haversineMeters = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  const handleArrived = async () => {
    if (!currentLivraison) return;
    if (currentLivraison.heure_arrivee) {
      toast.info("Arrivée déjà enregistrée");
      return;
    }
    setArriving(true);
    const threshold = Number(params?.gps_arrival_threshold_m ?? 200);
    const postArrived = async (lat = null, lng = null, dist = null, force = false) => {
      try {
        await axios.post(
          `${API}/livraisons/${currentLivraison.id}/arrived`,
          { lat, lng, distance_m: dist, force },
          { headers }
        );
        toast.success("✅ Arrivée enregistrée");
        await fetchTourneeDetails(selectedTournee);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur");
      } finally {
        setArriving(false);
      }
    };

    if (!navigator.geolocation) {
      // Pas de GPS — enregistre sans coordonnées
      await postArrived();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const targetLat = currentLivraison.latitude;
        const targetLng = currentLivraison.longitude;
        if (targetLat == null || targetLng == null) {
          await postArrived(lat, lng, null);
          return;
        }
        const dist = haversineMeters(lat, lng, targetLat, targetLng);
        if (dist > threshold) {
          const ok = window.confirm(
            `Tu es à ${Math.round(dist)} m de l'adresse (seuil ${threshold} m). Confirmer quand même l'arrivée ?`
          );
          if (!ok) {
            setArriving(false);
            return;
          }
          await postArrived(lat, lng, dist, true);
        } else {
          await postArrived(lat, lng, dist, false);
        }
      },
      async () => {
        // Refus GPS ou timeout — on enregistre sans coordonnées
        toast.warning("GPS indisponible — arrivée enregistrée sans position");
        await postArrived();
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    );
  };

  // Marque le canal (sms/email) comme envoyé côté serveur — best-effort
  const markReceiptSent = async (livraisonId, channel) => {
    try {
      await axios.post(
        `${API}/livraisons/${livraisonId}/mark-receipt-sent?channel=${channel}`,
        null,
        { headers }
      );
    } catch { /* silent */ }
  };

  // Auto envoi email + refresh statut
  const triggerAutoReceiptEmail = async (livraisonId) => {
    try {
      const res = await axios.post(
        `${API}/livraisons/${livraisonId}/send-email`,
        {},
        { headers }
      );
      if (res.data?.already_sent) {
        toast.info("Reçu email déjà envoyé");
      } else {
        toast.success(`✅ Reçu envoyé à ${res.data?.to || "client"}`);
      }
      await fetchTourneeDetails(selectedTournee);
    } catch (err) {
      // Silencieux si pas d'email disponible ; sinon toast erreur
      const detail = err.response?.data?.detail || "";
      if (detail.includes("email")) {
        toast.warning("Pas d'email client — reçu non envoyé automatiquement");
      } else if (detail) {
        toast.error(detail);
      }
    }
  };

  const resendReceiptEmailForce = async (livraisonId) => {
    setResendingReceipt(true);
    try {
      const res = await axios.post(
        `${API}/livraisons/${livraisonId}/send-email`,
        { force: true },
        { headers }
      );
      toast.success(`Reçu renvoyé à ${res.data?.to || "client"}`);
      await fetchTourneeDetails(selectedTournee);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setResendingReceipt(false);
    }
  };

  // Download receipt PDF
  const downloadReceipt = async (livraisonId) => {
    try {
      const response = await axios.get(`${API}/livraisons/${livraisonId}/recu`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `recu_${livraisonId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du telechargement");
    }
  };

  // Send receipt via SMS (opens native SMS app)
  const sendReceiptSMS = (livraison) => {
    if (!livraison?.client_telephone) {
      toast.error("Pas de numero de telephone");
      return;
    }
    const montant = livraison.montant_encaisse || livraison.prix || 0;
    const message = encodeURIComponent(
      `Bonjour ${livraison.client_nom.split(" ")[0]}, voici votre recu de livraison TopCocoon.\n` +
      `Articles: ${livraison.quantite}x ${livraison.details_commande || "articles"}\n` +
      `Montant: ${montant} EUR\n` +
      `Merci pour votre confiance !`
    );
    window.location.href = `sms:${livraison.client_telephone}?body=${message}`;
    // Marque SMS envoyé côté serveur (best-effort — sms: URI ne remonte pas de statut)
    markReceiptSent(livraison.id, "sms");
  };

  // Send receipt via Email — Envoi serveur Resend avec PDF joint
  const sendReceiptEmail = async (livraison) => {
    if (!livraison?.id) return;
    // 1) Résoudre l'email : livraison, fiche client, ou prompt
    let email = livraison?.client_email || "";
    if (!email && livraison?.client_telephone) {
      try {
        const res = await axios.get(`${API}/clients?telephone=${encodeURIComponent(livraison.client_telephone)}`, { headers });
        const c = Array.isArray(res.data) ? res.data.find(x => x.telephone === livraison.client_telephone) : null;
        email = c?.email || "";
      } catch { /* client fetch optional */ }
    }
    if (!email) {
      email = window.prompt("Email du client :", "");
      if (!email) return;
    }
    // 2) Envoi serveur Resend (PDF joint automatiquement)
    try {
      toast.info("Envoi en cours…");
      const res = await axios.post(`${API}/livraisons/${livraison.id}/send-email`, { email }, { headers });
      toast.success(`✅ Reçu envoyé à ${res.data.to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur envoi email");
    }
  };

  // SMS "J'arrive" notification
  const sendArrivalSMS = () => {
    if (!currentLivraison?.client_telephone) {
      toast.error("Pas de numero de telephone");
      return;
    }
    const firstName = currentLivraison.client_nom.split(" ")[0];
    const message = encodeURIComponent(
      `Bonjour ${firstName}, votre livreur TopCocoon sera chez vous dans environ 15 minutes. A tres bientot !`
    );
    window.location.href = `sms:${currentLivraison.client_telephone}?body=${message}`;
  };

  const copyTrackingLink = () => {
    const numCmd = currentLivraison?.numero_commande;
    if (!numCmd) {
      toast.error("Numéro de commande non disponible");
      return;
    }
    const url = `https://topcocoon.be/suivi?n=${numCmd}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Lien copié !")).catch(() => {
      // Fallback for mobile
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      toast.success("Lien copié !");
    });
  };

  // Report delivery
  const handleReport = async () => {
    if (!currentLivraison) return;
    setReporting(true);
    try {
      const res = await axios.post(`${API}/livraisons/${currentLivraison.id}/reporter`, {
        raison: reportReason,
        note: reportNote,
        date_souhaitee: reportDateSouhaitee || null,
        creer_prochaine: reportCreerProchaine,
      }, { headers });
      if (res.data.nouveau_numero) {
        toast.success(`Reportée — prochaine livraison créée (commande #${res.data.nouveau_numero})`);
      } else {
        toast.success("Livraison reportée");
      }
      setReportDialogOpen(false);
      setReportReason("");
      setReportNote("");
      setReportDateSouhaitee("");
      setReportCreerProchaine(true);
      await fetchTourneeDetails(selectedTournee);
      if (currentIndex < totalStops - 1) setCurrentIndex(currentIndex + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setReporting(false);
    }
  };

  // Saisie frais (essence, adblue, péage...) depuis le terrain
  const handleAddFrais = async () => {
    if (!selectedTournee || !fraisMontant) return;
    setFraisSubmitting(true);
    try {
      await axios.post(`${API}/tournees/${selectedTournee}/frais`, {
        type: fraisType,
        montant: parseFloat(fraisMontant),
        note: fraisNote || "",
      }, { headers });
      toast.success(`Frais enregistré : ${parseFloat(fraisMontant).toFixed(2)} €`);
      setFraisDialogOpen(false);
      setFraisMontant("");
      setFraisNote("");
      setFraisType("essence");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setFraisSubmitting(false);
    }
  };

  // ===== Échange =====
  const openExchangeDialog = async () => {
    if (produits.length === 0) {
      try {
        const res = await axios.get(`${API}/produits`, { headers });
        setProduits(res.data || []);
      } catch { /* ignore */ }
    }
    setExchangeRapportees([]);
    setExchangeReintegrer(true);
    setExchangeFraisLivraison("25");
    setExchangeDifference("0");
    setExchangeRaison("");
    setExchangeDialogOpen(true);
  };

  // ===== Modifier les articles (substitution sur place) =====
  const openModifyArticles = async () => {
    let prods = produits;
    if (prods.length === 0) {
      try { const res = await axios.get(`${API}/produits`, { headers }); prods = res.data || []; setProduits(prods); } catch { /* ignore */ }
    }
    const pls = (currentLivraison?.produits_lies || []).map((pl) => {
      const prod = prods.find((p) => p.sku === pl.sku);
      return {
        produit_id: prod?.id || "", sku: pl.sku || "", modele: pl.modele || "", couleur: pl.couleur || "",
        quantite: pl.quantite || 1, prix_unitaire: pl.prix_unitaire || (prod?.prix_b2c_ttc || 0),
      };
    });
    setModifyItems(pls.length ? pls : [{ produit_id: "", sku: "", modele: "", couleur: "", quantite: 1, prix_unitaire: 0 }]);
    setModifyDialogOpen(true);
  };

  const handleModifyArticles = async () => {
    if (!currentLivraison) return;
    const valid = modifyItems.filter((it) => it.sku);
    if (valid.length === 0) { toast.error("Ajoute au moins un article (choisis le modèle)"); return; }
    setModifySubmitting(true);
    try {
      const subtotal = valid.reduce((a, it) => a + (parseFloat(it.prix_unitaire) || 0) * (parseInt(it.quantite) || 1), 0);
      const frais = currentLivraison.frais_livraison || 0;
      const prix = Math.round((subtotal + frais) * 100) / 100;
      const qte = valid.reduce((a, it) => a + (parseInt(it.quantite) || 1), 0);
      const details = valid.map((it) => `${it.quantite}x ${it.modele} ${it.couleur}`.trim()).join(" + ");
      await axios.put(`${API}/livraisons/${currentLivraison.id}`, {
        produits_lies: valid.map((it) => ({
          sku: it.sku, modele: it.modele, couleur: it.couleur,
          quantite: parseInt(it.quantite) || 1, prix_unitaire: parseFloat(it.prix_unitaire) || 0,
        })),
        prix, quantite: qte, details_commande: details,
      }, { headers });
      toast.success(`Articles modifiés — nouveau total ${prix.toFixed(2)} €`);
      setModifyDialogOpen(false);
      await fetchTourneeDetails(selectedTournee);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setModifySubmitting(false);
    }
  };

  const handleExchange = async () => {
    if (!currentLivraison) return;
    if (exchangeRapportees.length === 0) {
      toast.error("Ajoute au moins une chaise rapportée");
      return;
    }
    setExchangeSubmitting(true);
    try {
      const res = await axios.post(`${API}/livraisons/${currentLivraison.id}/exchange`, {
        chaises_rapportees: exchangeRapportees.map(c => ({
          produit_id: c.produit_id || "",
          sku: c.sku || "",
          modele: c.modele || "",
          couleur: c.couleur || "",
          quantite: parseInt(c.quantite) || 1,
        })),
        reintegrer_stock: exchangeReintegrer,
        frais_livraison: parseFloat(exchangeFraisLivraison) || 0,
        difference_prix: parseFloat(exchangeDifference) || 0,
        raison: exchangeRaison || "",
      }, { headers });
      toast.success(`Échange enregistré ${res.data.montant_encaisse > 0 ? `— ${res.data.montant_encaisse}€ encaissés` : "(neutre)"}`);
      setExchangeDialogOpen(false);
      await fetchTourneeDetails(selectedTournee);
      if (currentIndex < totalStops - 1) setCurrentIndex(currentIndex + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur échange");
    } finally {
      setExchangeSubmitting(false);
    }
  };


  const openNavigation = (app) => {
    if (!currentLivraison) return;
    const { latitude, longitude } = currentLivraison;
    const fullAddress = encodeURIComponent(`${currentLivraison.adresse}, ${currentLivraison.code_postal} ${currentLivraison.ville}`);
    let url = "";
    if (app === "google") url = latitude && longitude ? `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}` : `https://www.google.com/maps/search/?api=1&query=${fullAddress}`;
    else if (app === "waze") url = latitude && longitude ? `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes` : `https://waze.com/ul?q=${fullAddress}&navigate=yes`;
    else if (app === "apple") url = latitude && longitude ? `http://maps.apple.com/?daddr=${latitude},${longitude}` : `http://maps.apple.com/?daddr=${fullAddress}`;
    else url = `https://www.google.com/maps/search/?api=1&query=${fullAddress}`;
    window.open(url, "_blank");
  };

  const callClient = () => {
    if (currentLivraison?.client_telephone) window.location.href = `tel:${currentLivraison.client_telephone}`;
    else toast.error("Pas de numero de telephone");
  };

  const handleStartTournee = async () => {
    if (!selectedTournee) return;
    // Charger les véhicules et ouvrir le popup km
    try {
      const res = await axios.get(`${API}/vehicules`, { headers });
      setVehicules(res.data || []);
      const defaultVeh = (res.data || [])[0];
      setKmForm({
        vehicule_id: defaultVeh?.id || "",
        km_depart: defaultVeh?.kilometrage || "",
        photo_depart: "",
        conducteur: user?.email?.split("@")[0] || "",
      });
      setKmDialogOpen(true);
    } catch {
      // Si erreur véhicules, on démarre quand même
      await actuallyStartTournee();
    }
  };

  const actuallyStartTournee = async () => {
    try {
      await axios.put(`${API}/tournees/${selectedTournee}`, { statut: "en_cours" }, { headers });
      toast.success("Tournee demarree !");
      setTourneeStatut("en_cours");
      fetchTournees();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleSaveKmAndStart = async () => {
    if (!kmForm.vehicule_id) { toast.error("Choisis un véhicule"); return; }
    if (!kmForm.km_depart) { toast.error("Kilométrage obligatoire"); return; }
    if (!kmForm.photo_depart) { toast.error("Photo du compteur obligatoire"); return; }
    setKmSubmitting(true);
    try {
      await axios.post(`${API}/vehicules/${kmForm.vehicule_id}/trips`, {
        conducteur: kmForm.conducteur || "livreur",
        type_utilisation: "livraison",
        destination: "Tournée",
        km_depart: Number(kmForm.km_depart),
        carburant_depart: 100,
        adblue_depart: "ok",
        remarques_depart: `Tournée ${selectedTournee}`,
        photo_depart: kmForm.photo_depart,
      }, { headers });
      toast.success("Carnet ouvert ✅");
      setKmDialogOpen(false);
      await actuallyStartTournee();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur enregistrement km");
    } finally { setKmSubmitting(false); }
  };

  const handleSkipKm = async () => {
    toast.warning("⚠️ Km non enregistré — n'oublie pas de compléter le carnet plus tard !", { duration: 6000 });
    setKmDialogOpen(false);
    await actuallyStartTournee();
  };

  // Ouvrir le popup Fin de tournée : trouve le trip en cours, préremplit km
  const handleOpenFinTournee = async () => {
    try {
      const res = await axios.get(`${API}/vehicules`, { headers });
      const vehs = res.data || [];
      let found = null;
      for (const v of vehs) {
        const openTrip = (v.trips || []).find(t => t.statut === "en_cours");
        if (openTrip) { found = { vehicule_id: v.id, trip_id: openTrip.id, km_depart: openTrip.km_depart || 0 }; break; }
      }
      setOpenTripInfo(found);
      setFinTourneeForm({
        km_retour: found ? String(found.km_depart || "") : "",
        photo_retour: "",
        remarques: "",
      });
      setFinTourneeOpen(true);
    } catch {
      setOpenTripInfo(null);
      setFinTourneeForm({ km_retour: "", photo_retour: "", remarques: "" });
      setFinTourneeOpen(true);
    }
  };

  const handleSubmitFinTournee = async () => {
    if (!finTourneeForm.km_retour || isNaN(Number(finTourneeForm.km_retour))) {
      toast.error("Kilométrage obligatoire (nombre)"); return;
    }
    if (!finTourneeForm.photo_retour) { toast.error("Photo obligatoire"); return; }
    setFinTourneeSubmitting(true);
    try {
      // 1) Clôturer le trip véhicule si présent
      if (openTripInfo?.vehicule_id && openTripInfo?.trip_id) {
        try {
          await axios.put(
            `${API}/vehicules/${openTripInfo.vehicule_id}/trips/${openTripInfo.trip_id}/close`,
            {
              km_retour: Number(finTourneeForm.km_retour),
              photo_retour: finTourneeForm.photo_retour,
              remarques_retour: finTourneeForm.remarques || "Fin de tournée",
            },
            { headers }
          );
        } catch (err) {
          toast.error(err.response?.data?.detail || "Erreur clôture carnet");
          setFinTourneeSubmitting(false);
          return;
        }
      }
      // 2) Marquer la tournée comme terminée
      if (selectedTournee) {
        await axios.put(`${API}/tournees/${selectedTournee}`, { statut: "terminee" }, { headers });
      }
      toast.success("Tournée terminée ✅");
      setFinTourneeOpen(false);
      setTourneeStatut("terminee");
      fetchTournees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setFinTourneeSubmitting(false);
    }
  };

  // Modifier le prix sur place — remises séparées Chaises / Livraison
  const [modifyPrixOpen, setModifyPrixOpen] = useState(false);
  const [modifyPrixForm, setModifyPrixForm] = useState({ prix_chaise: 0, frais_livraison: 0, raison: "" });
  const [modifyPrixInitial, setModifyPrixInitial] = useState({ prix_chaise: 0, frais_livraison: 0, total_chaises: 0 });
  const [savingPrix, setSavingPrix] = useState(false);

  const openModifyPrix = () => {
    if (!currentLivraison) return;
    const pl = currentLivraison.produits_lies || [];
    const totalChaises = pl.reduce((s, p) => s + (p.quantite || 0), 0) || 1;
    const fraisLiv = currentLivraison.frais_livraison ?? 25;
    // Prix chaise unitaire : moyenne pondérée si absent, sinon 1er non nul
    let prixChaiseUnit = 0;
    const withUnit = pl.find(p => p.prix_unitaire);
    if (withUnit) {
      prixChaiseUnit = withUnit.prix_unitaire;
    } else {
      // Déduction depuis total : (prix - frais) / total_chaises
      const restant = (currentLivraison.prix || 0) - fraisLiv;
      prixChaiseUnit = totalChaises > 0 ? Math.max(0, +(restant / totalChaises).toFixed(2)) : 0;
    }
    setModifyPrixInitial({ prix_chaise: prixChaiseUnit, frais_livraison: fraisLiv, total_chaises: totalChaises });
    setModifyPrixForm({ prix_chaise: prixChaiseUnit, frais_livraison: fraisLiv, raison: "" });
    setModifyPrixOpen(true);
  };

  const saveModifyPrix = async () => {
    if (!currentLivraison) return;
    const { prix_chaise, frais_livraison, raison } = modifyPrixForm;
    if (!raison || !raison.trim()) { toast.error("Raison obligatoire"); return; }
    const pChaise = Number(prix_chaise);
    const fraisLiv = Number(frais_livraison);
    if (isNaN(pChaise) || pChaise < 0 || isNaN(fraisLiv) || fraisLiv < 0) { toast.error("Prix invalide"); return; }
    const nouveauTotalChaises = pChaise * modifyPrixInitial.total_chaises;
    const nouveauTotal = nouveauTotalChaises + fraisLiv;
    // Nouveaux produits_lies avec prix_unitaire mis à jour
    const nouveauxProduitsLies = (currentLivraison.produits_lies || []).map(p => ({
      ...p,
      prix_unitaire: pChaise,
    }));
    // Remises calculées
    const remiseChaises = Math.max(0, (modifyPrixInitial.prix_chaise - pChaise) * modifyPrixInitial.total_chaises);
    const remiseLivraison = Math.max(0, modifyPrixInitial.frais_livraison - fraisLiv);
    const remiseTotale = remiseChaises + remiseLivraison;
    setSavingPrix(true);
    try {
      await axios.put(`${API}/livraisons/${currentLivraison.id}`, {
        prix: nouveauTotal,
        montant_encaisse: nouveauTotal,
        frais_livraison: fraisLiv,
        produits_lies: nouveauxProduitsLies,
        note_encaissement: `Remise ${remiseTotale.toFixed(2)}€ (🪑 ${remiseChaises.toFixed(2)}€ · 🚚 ${remiseLivraison.toFixed(2)}€) — ${raison}`,
      }, { headers });
      toast.success(`Nouveau total : ${nouveauTotal.toFixed(2)} € (remise -${remiseTotale.toFixed(2)}€)`);
      setModifyPrixOpen(false);
      await fetchTourneeDetails(selectedTournee);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSavingPrix(false); }
  };

  // Annuler la livraison sur place (client refuse / doublon / patron a annulé)
  const openCancelDialog = async () => {
    if (!currentLivraison) return;
    if (!window.confirm(`Annuler la livraison pour ${currentLivraison.client_nom} ?\n\nElle sera marquée comme annulée.`)) return;
    const raison = window.prompt("Raison de l'annulation (ex: client refuse, patron a annulé, doublon…) :", "");
    if (!raison || !raison.trim()) { toast.error("Raison obligatoire"); return; }
    try {
      await axios.put(`${API}/livraisons/${currentLivraison.id}`, {
        statut: "annulee",
        notes: `❌ Annulée par livreur — ${raison}`,
      }, { headers });
      toast.success("Livraison annulée");
      await fetchTourneeDetails(selectedTournee);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const uploadKmPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setKmForm(prev => ({ ...prev, photo_depart: res.data.id }));
      toast.success("Photo compteur ajoutée");
    } catch { toast.error("Erreur upload photo"); }
  };

  const uploadFinTourneePhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setFinTourneeForm(prev => ({ ...prev, photo_retour: res.data.id }));
      toast.success("Photo ajoutée");
    } catch { toast.error("Erreur upload photo"); }
  };

  const navigateToDepot = () => {
    if (!tourneeDetails) return;
    const { lat, lng } = tourneeDetails.point_depart;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, "_blank");
  };

  const totalPrevu = tourneeDetails?.livraisons.reduce((s, l) => s + (l.prix || 0), 0) || 0;
  const totalEncaisse = tourneeDetails?.livraisons.filter((l) => l.statut === "livree").reduce((s, l) => s + (l.montant_encaisse || l.prix || 0), 0) || 0;

  // Récap fin de tournée : cash / virement / reportées / annulées
  const tourneeSummary = (() => {
    const livs = tourneeDetails?.livraisons || [];
    const livrees = livs.filter((l) => l.statut === "livree");
    const reportees = livs.filter((l) => l.statut === "reportee");
    const annulees = livs.filter((l) => l.statut === "annulee");
    const cash = livrees
      .filter((l) => l.mode_paiement === "liquide")
      .reduce((s, l) => s + (l.montant_encaisse || 0), 0);
    const virement = livrees
      .filter((l) => l.mode_paiement === "virement")
      .reduce((s, l) => s + (l.montant_encaisse || 0), 0);
    return {
      livreesCount: livrees.length,
      reporteesCount: reportees.length,
      annuleesCount: annulees.length,
      cash,
      virement,
      total: cash + virement,
    };
  })();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (tournees.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 p-4">
        <Truck className="w-16 h-16 text-slate-400 mb-4" />
        <h2 className="text-xl font-bold text-slate-700 mb-2">Aucune tournee active</h2>
        <p className="text-slate-500 text-center mb-6">
          {isLivreur ? "Aucune tournée ne vous a encore été assignée pour aujourd'hui." : "Creez une tournee depuis la page Tournees."}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowQrModal(true)} data-testid="qr-btn-empty">
            <QrCode className="w-4 h-4 mr-2" />QR TopCocoon
          </Button>
          {isLivreur ? (
            <Button variant="outline" onClick={() => { logout(); navigate("/login"); }} data-testid="livreur-logout-empty-btn">
              <LogOut className="w-4 h-4 mr-2" />Se déconnecter
            </Button>
          ) : (
            <a href="/tournees"><Button variant="outline" data-testid="go-tournees-btn"><ArrowLeft className="w-4 h-4 mr-2" />Retour aux Tournees</Button></a>
          )}
        </div>
        {/* QR Modal (also rendered here) */}
        <QrTabsModal open={showQrModal} onClose={() => setShowQrModal(false)} user={user} params={params} />
      </div>
    );
  }

  const prixPrevu = currentLivraison?.prix;
  const encaisseNum = montantEncaisse ? parseFloat(montantEncaisse) : null;
  const diff = prixPrevu != null && encaisseNum != null ? encaisseNum - prixPrevu : null;

  // Find the last delivered livraison for receipt dialog
  const lastDelivered = tourneeDetails?.livraisons.find((l) => l.id === lastDeliveredId);

  return (
    <div className="min-h-screen bg-slate-100 pb-safe" data-testid="livreur-page">
      {/* Header */}
      <div className="bg-indigo-600 text-white p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {isLivreur ? (
              <button
                onClick={() => { logout(); navigate("/login"); }}
                className="p-1 hover:bg-white/10 rounded-lg"
                title="Se déconnecter"
                data-testid="livreur-logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            ) : (
              <a href="/tournees" className="p-1 hover:bg-white/10 rounded-lg" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></a>
            )}
            <Truck className="w-6 h-6" />
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-lg">Mode Livreur</span>
              {isLivreur && user?.name && (
                <span className="text-xs text-white/80" data-testid="livreur-name">{user.name}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowQrModal(true)}
              className="p-2 hover:bg-white/10 rounded-lg"
              title="Afficher QR TopCocoon"
              data-testid="qr-btn"
            >
              <QrCode className="w-5 h-5" />
            </button>
            <button onClick={() => setShowStopsList(!showStopsList)} className="p-2 hover:bg-white/10 rounded-lg" data-testid="toggle-list-btn">
              {showStopsList ? <X className="w-5 h-5" /> : <List className="w-5 h-5" />}
            </button>
            <Badge className="bg-white/20 text-white">{deliveredCount}/{totalStops}</Badge>
          </div>
        </div>

        <Select value={selectedTournee} onValueChange={(val) => { setSelectedTournee(val); setShowStopsList(false); }}>
          <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="tournee-selector"><SelectValue placeholder="Selectionner une tournee" /></SelectTrigger>
          <SelectContent>
            {tournees.map((t) => (<SelectItem key={t.id} value={t.id}>{t.nom} ({t.livraisons_ids?.length || 0} arrets)</SelectItem>))}
          </SelectContent>
        </Select>

        {tourneeDetails && totalStops > 0 && (
          <div className="mt-3">
            <div className="flex justify-between text-sm mb-1"><span>Progression</span><span>{Math.round((deliveredCount / totalStops) * 100)}%</span></div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-green-400 transition-all duration-300" style={{ width: `${(deliveredCount / totalStops) * 100}%` }} />
            </div>
            {totalPrevu > 0 && (
              <div className="flex justify-between text-xs mt-1 opacity-80">
                <span>Encaisse: {totalEncaisse.toFixed(0)}EUR</span>
                <span>Total prevu: {totalPrevu.toFixed(0)}EUR</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Start Tournee Banner */}
      {tourneeStatut === "planifiee" && (
        <div className="mx-4 mt-4" data-testid="start-tournee-banner">
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Play className="w-6 h-6 text-amber-600" />
                <div><h3 className="font-bold text-amber-800">Tournee planifiee</h3><p className="text-sm text-amber-600">Demarrez la tournee pour commencer</p></div>
              </div>
              <Button size="lg" className="w-full h-14 text-lg bg-amber-600 hover:bg-amber-700" onClick={handleStartTournee} data-testid="start-tournee-btn">
                <Play className="w-5 h-5 mr-2" />DEMARRER LA TOURNEE
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stops List */}
      {showStopsList && tourneeDetails && (
        <div className="mx-4 mt-4 mb-4" data-testid="stops-list">
          <Card>
            <CardContent className="p-3">
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2"><List className="w-4 h-4" />Tous les arrets ({totalStops})</h3>
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {tourneeDetails.livraisons.map((l, idx) => (
                  <button key={l.id} onClick={() => { setCurrentIndex(idx); setShowStopsList(false); }}
                    className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${idx === currentIndex ? "bg-indigo-50 border border-indigo-200" : l.statut === "livree" ? "bg-green-50 border border-green-100" : "bg-slate-50 hover:bg-slate-100 border border-transparent"}`}
                    data-testid={`stop-item-${idx}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${l.statut === "livree" ? "bg-green-500 text-white" : idx === currentIndex ? "bg-indigo-600 text-white" : "bg-slate-300 text-slate-700"}`}>
                      {l.statut === "livree" ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${l.statut === "livree" ? "text-green-700 line-through" : "text-slate-800"}`}>{l.client_nom}</p>
                      <p className="text-xs text-slate-500 truncate">{l.adresse}</p>
                    </div>
                    {l.prix != null && <span className="text-sm font-medium text-slate-600">{l.montant_encaisse != null && l.statut === "livree" ? `${l.montant_encaisse}EUR` : `${l.prix}EUR`}</span>}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content */}
      {!showStopsList && (
        <div className="p-4">
          {tourneeDetails && currentLivraison ? (
            <>
              <div className="text-center mb-4">
                <span className="text-slate-500">Arret</span>
                <span className="mx-2 text-2xl font-bold text-indigo-600">{currentIndex + 1}</span>
                <span className="text-slate-500">sur {totalStops}</span>
              </div>

              <Card className={`mb-4 ${currentLivraison.statut === "livree" ? "bg-green-50 border-green-200" : ""}`}>
                <CardContent className="p-5">
                  {currentLivraison.statut === "livree" && (
                    <div className="flex items-center justify-center gap-2 mb-4 text-green-600">
                      <CheckCircle className="w-5 h-5" /><span className="font-medium">Livraison effectuee</span>
                    </div>
                  )}

                  {/* Encart B2B — informations société source & paiement */}
                  {currentLivraison.b2b_source_id && (
                    <div className="mb-4 p-3 rounded-lg border-2 border-purple-300 bg-purple-50" data-testid="livreur-b2b-banner">
                      <div className="flex items-start gap-2">
                        <Building2 className="w-5 h-5 text-purple-700 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-purple-900 uppercase tracking-wide">
                            {currentLivraison.livrer_chez === "client_final" ? "🎭 Livrer au nom de :" : "Commande B2B —"} {currentLivraison.b2b_source_nom || "Client pro"}
                          </p>
                          {currentLivraison.livrer_chez === "client_final" && (
                            <p className="text-[11px] text-purple-800 bg-purple-100 rounded px-2 py-1 mt-1 font-medium">
                              ⚠️ Whitelabel : dis « je livre pour <strong>{currentLivraison.b2b_source_nom}</strong> » — <strong>NE mentionne PAS TopCocoon</strong>
                            </p>
                          )}
                          {currentLivraison.statut_paiement === "paye" ? (
                            <p className="text-sm font-bold text-emerald-700 mt-2">✅ TOUT PAYÉ EN LIGNE — aucun encaissement</p>
                          ) : (currentLivraison.a_encaisser || 0) > 0 ? (
                            <div className="mt-2 bg-amber-100 border border-amber-300 rounded p-2">
                              <p className="text-sm font-bold text-amber-900">💰 À encaisser sur place : {currentLivraison.a_encaisser.toFixed(2)} €</p>
                              <p className="text-[10px] text-amber-700">Chaises déjà réglées par le B2B ({(currentLivraison.paye_par_b2b || 0).toFixed(2)} € virement)</p>
                            </div>
                          ) : (
                            <p className="text-sm font-bold text-emerald-700 mt-2">✅ Payé par virement B2B ({(currentLivraison.paye_par_b2b || 0).toFixed(2)} €) — rien à encaisser</p>
                          )}
                          <div className="mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 bg-white"
                              onClick={async () => {
                                try {
                                  const r = await fetch(`${API}/livraisons/${currentLivraison.id}/bon-livraison`, {
                                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                                  });
                                  const html = await r.text();
                                  const w = window.open("", "_blank");
                                  w.document.write(html);
                                  w.document.close();
                                } catch { toast.error("Erreur"); }
                              }}
                              data-testid="livreur-bon-livraison"
                            >
                              🖨️ Imprimer bon de livraison
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center"><User className="w-6 h-6 text-indigo-600" /></div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-800" data-testid="client-name">{currentLivraison.client_nom}</h2>
                      {currentLivraison.client_telephone && (
                        <p className="text-slate-500 flex flex-wrap gap-2 items-center" data-testid="client-phone">
                          <a href={`tel:${currentLivraison.client_telephone}`} className="text-indigo-600 font-medium">📞 {currentLivraison.client_telephone}</a>
                          <a href={`sms:${currentLivraison.client_telephone}`} className="text-emerald-600">💬 SMS</a>
                          <a href={`https://wa.me/${(currentLivraison.client_telephone || '').replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="text-green-600">🟢 WhatsApp</a>
                        </p>
                      )}
                      {currentLivraison.client_email && (
                        <p className="text-slate-500"><a href={`mailto:${currentLivraison.client_email}`} className="text-indigo-600">📧 {currentLivraison.client_email}</a></p>
                      )}
                      {currentLivraison.client_email && <p className="text-slate-500 text-sm" data-testid="client-email">{currentLivraison.client_email}</p>}
                    </div>
                  </div>

                  <div className="flex items-start gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800" data-testid="client-address">{currentLivraison.adresse}</p>
                      <p className="text-slate-600">{currentLivraison.code_postal} {currentLivraison.ville}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 mb-4 p-3 bg-amber-50 rounded-lg">
                    <Package className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800" data-testid="order-qty">{currentLivraison.quantite}x articles</p>
                      {currentLivraison.details_commande && <p className="text-amber-700" data-testid="order-details">{currentLivraison.details_commande}</p>}
                    </div>
                  </div>

                  {currentLivraison.notes && (
                    <div className="flex items-start gap-3 mb-4 p-3 bg-blue-50 rounded-lg">
                      <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-blue-800">{currentLivraison.notes}</p>
                    </div>
                  )}

                  {/* Encaissement section */}
                  {prixPrevu != null && (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="encaissement-section">
                      <div className="flex items-center gap-2 mb-3">
                        <Euro className="w-5 h-5 text-emerald-700" />
                        <span className="font-bold text-emerald-800">Montant prevu : {prixPrevu} EUR</span>
                      </div>
                      {currentLivraison.statut !== "livree" ? (
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-emerald-700 mb-1 block">Montant encaisse (EUR)</label>
                            <Input type="number" step="0.01" value={montantEncaisse} onChange={(e) => setMontantEncaisse(e.target.value)} className="text-lg font-bold h-12 bg-white" placeholder={String(prixPrevu)} data-testid="montant-encaisse-input" />
                          </div>
                          {diff != null && diff !== 0 && (
                            <div className={`flex items-center gap-2 text-sm ${diff < 0 ? "text-orange-600" : "text-green-600"}`} data-testid="montant-diff">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">Difference : {diff > 0 ? "+" : ""}{diff.toFixed(2)} EUR</span>
                            </div>
                          )}
                          {diff != null && diff !== 0 && (
                            <Textarea value={noteEncaissement} onChange={(e) => setNoteEncaissement(e.target.value)} placeholder="Raison (ex: livraison offerte, chaise remplacee...)" rows={2} className="bg-white text-sm" data-testid="note-encaissement-input" />
                          )}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-lg font-bold text-emerald-800" data-testid="montant-encaisse-display">
                            Encaisse : {currentLivraison.montant_encaisse != null ? currentLivraison.montant_encaisse : prixPrevu} EUR
                          </p>
                          {currentLivraison.montant_encaisse != null && currentLivraison.montant_encaisse !== prixPrevu && (
                            <p className="text-sm text-orange-600 flex items-center gap-1">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              Difference : {(currentLivraison.montant_encaisse - prixPrevu).toFixed(2)} EUR
                              {currentLivraison.note_encaissement && ` — ${currentLivraison.note_encaissement}`}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Action buttons */}
              <div className="space-y-3 mb-4">
                {/* SMS + Call row */}
                {currentLivraison.statut !== "livree" && currentLivraison.client_telephone && (
                  <Button size="lg" variant="outline" className="w-full h-14 text-base border-indigo-200 text-indigo-700 hover:bg-indigo-50" onClick={sendArrivalSMS} data-testid="sms-arrival-btn">
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Prevenir le client (SMS)
                  </Button>
                )}

                {/* Copy tracking link */}
                {currentLivraison.suivi_token && (
                  <Button size="lg" variant="outline" className="w-full h-12 text-sm border-slate-200 text-slate-600 hover:bg-slate-50" onClick={copyTrackingLink} data-testid="copy-tracking-link-btn">
                    <Link2 className="w-4 h-4 mr-2" />
                    Copier le lien de suivi client
                  </Button>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Button size="lg" variant="outline" className="h-14 text-base" onClick={callClient} disabled={!currentLivraison.client_telephone} data-testid="call-btn">
                    <Phone className="w-5 h-5 mr-2" />Appeler
                  </Button>
                  <Select onValueChange={openNavigation}>
                    <SelectTrigger className="h-14 text-base bg-blue-600 text-white border-blue-600 hover:bg-blue-700" data-testid="navigate-btn">
                      <div className="flex items-center"><Navigation className="w-5 h-5 mr-2" />Naviguer</div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google"><div className="flex items-center gap-2"><Map className="w-4 h-4" />Google Maps</div></SelectItem>
                      <SelectItem value="waze"><div className="flex items-center gap-2"><Navigation className="w-4 h-4" />Waze</div></SelectItem>
                      <SelectItem value="apple"><div className="flex items-center gap-2"><MapPin className="w-4 h-4" />Apple Maps</div></SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Je suis arrivé — GPS one-tap */}
                {currentLivraison.statut !== "livree" && currentLivraison.statut !== "reportee" && currentLivraison.statut !== "annulee" && (
                  currentLivraison.heure_arrivee ? (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center" data-testid="arrived-badge">
                      <p className="text-sm font-semibold text-emerald-800">
                        📍 Arrivé à {new Date(currentLivraison.heure_arrivee).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        {currentLivraison.arrivee_distance_m != null && (
                          <span className="text-xs text-emerald-600 ml-2">({Math.round(currentLivraison.arrivee_distance_m)} m)</span>
                        )}
                      </p>
                    </div>
                  ) : (
                    <Button
                      size="lg"
                      className="w-full h-14 text-base bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleArrived}
                      disabled={arriving}
                      data-testid="arrived-btn"
                    >
                      {arriving ? (
                        <><span className="animate-spin mr-2">...</span>GPS…</>
                      ) : (
                        <><MapPin className="w-5 h-5 mr-2" />Je suis arrivé</>
                      )}
                    </Button>
                  )
                )}
              </div>

              {/* Mark as delivered - opens confirmation dialog (cash or virement) */}
              {currentLivraison.statut !== "livree" && currentLivraison.statut !== "reportee" && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <Button size="lg" className="h-16 text-base bg-green-600 hover:bg-green-700" onClick={() => { setPaymentMode("liquide"); handleLivreClick(); }} data-testid="mark-delivered-cash-btn">
                    <CheckCircle className="w-5 h-5 mr-1" />
                    LIVRÉ — Cash
                  </Button>
                  <Button size="lg" className="h-16 text-base bg-blue-600 hover:bg-blue-700" onClick={() => { setPaymentMode("virement"); handleLivreClick(); }} data-testid="mark-delivered-virement-btn">
                    <CheckCircle className="w-5 h-5 mr-1" />
                    LIVRÉ — Virement
                  </Button>
                </div>
              )}

              {currentLivraison.statut === "livree" && currentLivraison.mode_paiement && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-3 text-center" data-testid="payment-badge">
                  <p className="text-sm font-semibold text-green-800">
                    ✓ Livré — Payé en {currentLivraison.mode_paiement === "virement" ? "🏦 virement" : "💵 cash"}
                  </p>
                </div>
              )}

              {currentLivraison.statut === "reportee" && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3 text-center" data-testid="reportee-badge">
                  <p className="text-sm font-semibold text-orange-800 mb-2">↻ Livraison reportée</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/livraisons/${currentLivraison.id}`, { statut: "en_cours" }, { headers });
                        toast.success("Report annulé — la livraison est de nouveau active");
                        fetchTourneeDetails(selectedTournee);
                      } catch {
                        toast.error("Erreur lors de l'annulation du report");
                      }
                    }}
                    data-testid="undo-report-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Annuler le report
                  </Button>
                </div>
              )}

              {currentLivraison.statut !== "livree" && currentLivraison.statut !== "reportee" && (
                <Button size="lg" variant="outline" className="w-full h-12 text-base border-amber-300 text-amber-700 hover:bg-amber-50 mb-3" onClick={openModifyArticles} data-testid="modify-articles-btn">
                  <Package className="w-5 h-5 mr-2" />
                  MODIFIER LES ARTICLES
                </Button>
              )}

              {currentLivraison.statut !== "livree" && currentLivraison.statut !== "reportee" && (
                <Button size="lg" variant="outline" className="w-full h-12 text-base border-violet-300 text-violet-700 hover:bg-violet-50 mb-3" onClick={openExchangeDialog} data-testid="exchange-btn">
                  <Repeat className="w-5 h-5 mr-2" />
                  ÉCHANGE
                </Button>
              )}

              {currentLivraison.is_exchange && (
                <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg mb-3 text-sm" data-testid="exchange-badge">
                  <p className="font-bold text-violet-700 flex items-center gap-2">
                    <Repeat className="w-4 h-4" />
                    Échange enregistré
                  </p>
                  {currentLivraison.chaises_rapportees?.length > 0 && (
                    <p className="text-violet-600 mt-1">
                      Rapportées : {currentLivraison.chaises_rapportees.map(c => `${c.quantite}x ${c.modele} ${c.couleur}`.trim()).join(" + ")}
                    </p>
                  )}
                </div>
              )}

              {currentLivraison.statut !== "livree" && currentLivraison.statut !== "reportee" && (
                <Button size="lg" variant="outline" className="w-full h-12 text-base border-orange-200 text-orange-700 hover:bg-orange-50 mb-4" onClick={() => { setReportReason(""); setReportNote(""); setReportDialogOpen(true); }} data-testid="report-btn">
                  <RotateCcw className="w-5 h-5 mr-2" />
                  REPORTER
                </Button>
              )}

              {/* Modifier prix (remise dernière minute) */}
              {currentLivraison.statut !== "livree" && currentLivraison.statut !== "reportee" && currentLivraison.statut !== "annulee" && (
                <Button size="lg" variant="outline" className="w-full h-12 text-base border-blue-300 text-blue-700 hover:bg-blue-50 mb-3" onClick={openModifyPrix} data-testid="modify-prix-btn">
                  <Euro className="w-5 h-5 mr-2" />
                  MODIFIER LE PRIX
                </Button>
              )}

              {/* Annuler la livraison sur place (client refuse / doublon) */}
              {currentLivraison.statut !== "livree" && currentLivraison.statut !== "reportee" && currentLivraison.statut !== "annulee" && (
                <Button size="lg" variant="outline" className="w-full h-12 text-base border-red-300 text-red-700 hover:bg-red-50 mb-4" onClick={openCancelDialog} data-testid="cancel-livraison-btn">
                  <X className="w-5 h-5 mr-2" />
                  ANNULER LA LIVRAISON
                </Button>
              )}

              {currentLivraison.statut === "annulee" && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4 text-center" data-testid="cancelled-badge">
                  <p className="font-medium text-red-700 flex items-center justify-center gap-2">
                    <X className="w-4 h-4" />
                    Livraison annulée
                  </p>
                  {currentLivraison.notes && <p className="text-sm text-red-600 mt-1">{currentLivraison.notes}</p>}
                </div>
              )}

              {currentLivraison.statut === "reportee" && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-4 text-center" data-testid="reported-badge">
                  <p className="font-medium text-orange-700 flex items-center justify-center gap-2">
                    <RotateCcw className="w-4 h-4" />
                    Livraison reportee
                  </p>
                  {currentLivraison.notes && <p className="text-sm text-orange-600 mt-1">{currentLivraison.notes}</p>}
                </div>
              )}

              {/* Download receipt for already delivered */}
              {currentLivraison.statut === "livree" && (
                <div className="space-y-3 mb-4">
                  {/* Statuts d'envoi reçu — idempotence visible */}
                  <div className="grid grid-cols-2 gap-2" data-testid="receipt-status-inline">
                    <div className={`p-2 rounded-lg border text-center text-xs ${currentLivraison.receipt_email_status === "sent" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : currentLivraison.receipt_email_status === "failed" ? "bg-red-50 border-red-200 text-red-800" : currentLivraison.receipt_email_status === "not_available" ? "bg-slate-50 border-slate-200 text-slate-500" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                      <Mail className="w-3.5 h-3.5 inline mr-1" />
                      {currentLivraison.receipt_email_status === "sent" ? "Email envoyé ✓" :
                        currentLivraison.receipt_email_status === "failed" ? "Email échoué" :
                        currentLivraison.receipt_email_status === "not_available" ? "Pas d'email" :
                        "Email — en attente"}
                    </div>
                    <div className={`p-2 rounded-lg border text-center text-xs ${currentLivraison.receipt_sms_status === "sent" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                      <Send className="w-3.5 h-3.5 inline mr-1" />
                      {currentLivraison.receipt_sms_status === "sent" ? "SMS envoyé ✓" : "SMS — non envoyé"}
                    </div>
                  </div>
                  {currentLivraison.receipt_email_status === "failed" && (
                    <Button
                      size="lg"
                      className="w-full h-12 bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => resendReceiptEmailForce(currentLivraison.id)}
                      disabled={resendingReceipt}
                      data-testid="inline-resend-email-btn"
                    >
                      <RefreshCw className={`w-5 h-5 mr-2 ${resendingReceipt ? "animate-spin" : ""}`} />
                      Renvoyer le reçu email
                    </Button>
                  )}
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full h-12 border-amber-200 text-amber-700 hover:bg-amber-50"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/livraisons/${currentLivraison.id}`, { statut: "en_cours" }, { headers });
                        toast.success("Livraison remise en cours");
                        fetchTourneeDetails(selectedTournee);
                      } catch { toast.error("Erreur"); }
                    }}
                    data-testid="undo-delivery-livreur-btn"
                  >
                    <RefreshCw className="w-5 h-5 mr-2" />
                    Annuler la livraison
                  </Button>
                  <div className="grid grid-cols-2 gap-3">
                    <Button size="lg" variant="outline" className="h-12" onClick={() => downloadReceipt(currentLivraison.id)} data-testid="download-receipt-btn">
                      <Download className="w-5 h-5 mr-2" />Recu PDF
                    </Button>
                    <Button size="lg" variant="outline" className="h-12" onClick={() => sendReceiptSMS(currentLivraison)} data-testid="send-receipt-sms-btn">
                      <Send className="w-5 h-5 mr-2" />Envoyer SMS
                    </Button>
                  </div>
                </div>
              )}

              {/* Navigation arrows */}
              <div className="flex gap-3">
                <Button size="lg" variant="outline" className="flex-1 h-12" onClick={goToPrevious} disabled={currentIndex === 0} data-testid="prev-btn"><ChevronLeft className="w-5 h-5 mr-1" />Precedent</Button>
                <Button size="lg" variant="outline" className="flex-1 h-12" onClick={goToNext} disabled={currentIndex === totalStops - 1} data-testid="next-btn">Suivant<ChevronRight className="w-5 h-5 ml-1" /></Button>
              </div>

              {/* Return to depot */}
              {deliveredCount === totalStops && (
                <Card className="mt-4 bg-indigo-50 border-indigo-200">
                  <CardContent className="p-4">
                    <div className="text-center mb-3">
                      <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                      <h3 className="font-bold text-lg text-indigo-800" data-testid="tournee-complete-title">Tournee terminee !</h3>
                      <p className="text-indigo-600">Toutes les livraisons ont ete effectuees</p>
                      {totalPrevu > 0 && (
                        <div className="mt-3 grid grid-cols-2 gap-2 text-sm" data-testid="tournee-complete-recap">
                          <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-200">
                            <p className="text-[10px] uppercase text-emerald-700 font-semibold">💵 Cash</p>
                            <p className="text-lg font-bold text-emerald-900">{tourneeSummary.cash.toFixed(2)} €</p>
                          </div>
                          <div className="p-2 rounded-lg bg-blue-50 border border-blue-200">
                            <p className="text-[10px] uppercase text-blue-700 font-semibold">🏦 Virement</p>
                            <p className="text-lg font-bold text-blue-900">{tourneeSummary.virement.toFixed(2)} €</p>
                          </div>
                          <div className="col-span-2 p-2 rounded-lg bg-indigo-100 border border-indigo-200">
                            <p className="text-xs text-indigo-700">Total encaissé</p>
                            <p className="text-lg font-bold text-indigo-900" data-testid="total-encaisse">
                              {tourneeSummary.total.toFixed(2)} € / {totalPrevu.toFixed(2)} € prévu
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    <Button size="lg" className="w-full h-14 bg-indigo-600 hover:bg-indigo-700" onClick={navigateToDepot} data-testid="return-depot-btn">
                      <Home className="w-5 h-5 mr-2" />Retour au depot
                    </Button>
                    <Button size="lg" className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 mt-2" onClick={handleOpenFinTournee} data-testid="fin-tournee-btn">
                      <CheckCircle className="w-5 h-5 mr-2" />Terminer la tournée
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Bouton Terminer visible aussi si des livraisons restent (accès rapide) */}
              {deliveredCount < totalStops && tourneeStatut === "en_cours" && (
                <div className="mt-4">
                  <Button variant="outline" size="lg" className="w-full h-12 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={handleOpenFinTournee} data-testid="fin-tournee-btn-anytime">
                    <CheckCircle className="w-5 h-5 mr-2" />Terminer la tournée maintenant
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Selectionnez une tournee pour commencer</p>
            </div>
          )}
        </div>
      )}

      {/* Delivery Confirmation Dialog with Signature */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md w-[calc(100%-1rem)] mx-2 sm:mx-4 p-0 gap-0 max-h-[100dvh] sm:max-h-[92vh] flex flex-col overflow-hidden" data-testid="confirm-delivery-dialog">
          <DialogHeader className="px-4 pt-4 pb-2 border-b flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Confirmation de livraison
            </DialogTitle>
          </DialogHeader>
          {currentLivraison && (
            <>
            <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 space-y-3 sm:space-y-4">
              <div className="p-2.5 sm:p-3 bg-slate-50 rounded-lg">
                <p className="font-bold text-slate-800 text-sm sm:text-base">{currentLivraison.client_nom}</p>
                <p className="text-xs sm:text-sm text-slate-500">{currentLivraison.quantite}x {currentLivraison.details_commande || "articles"}</p>
                {prixPrevu != null && (
                  <p className="text-xs sm:text-sm font-medium mt-1">
                    Montant : <span className="text-emerald-700">{encaisseNum != null ? encaisseNum : prixPrevu} EUR</span>
                    {diff != null && diff !== 0 && <span className="text-orange-500 ml-2">(prevu: {prixPrevu} EUR)</span>}
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2 block">Signature du client :</label>
                <SignatureCanvas onSignatureChange={setSignatureData} />
              </div>

              {/* Virement — choix du bénéficiaire */}
              {paymentMode === "virement" && (
                <div className="p-2.5 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="virement-beneficiaire-section">
                  <label className="text-xs sm:text-sm font-semibold text-blue-800 mb-1.5 sm:mb-2 block">🏦 Virement reçu sur quel compte ?</label>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setVirementBeneficiaire("cocoon")}
                      className={`px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${virementBeneficiaire === "cocoon" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200"}`}
                      data-testid="virement-cocoon-btn"
                    >
                      🏢 Cocoon-Elec
                    </button>
                    <button
                      type="button"
                      onClick={() => setVirementBeneficiaire("autre")}
                      className={`px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${virementBeneficiaire === "autre" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200"}`}
                      data-testid="virement-autre-btn"
                    >
                      👤 Autre (livreur)
                    </button>
                  </div>
                  {virementBeneficiaire === "autre" && (
                    <Input
                      placeholder="Nom du livreur (ex: Ahmed)"
                      value={virementLivreurNom}
                      onChange={(e) => setVirementLivreurNom(e.target.value)}
                      className="bg-white h-9 text-sm"
                      data-testid="virement-livreur-nom-input"
                    />
                  )}
                </div>
              )}

              {/* Source d'acquisition + Photo — regroupés en "Détails optionnels" repliables (mobile-friendly) */}
              <div className="border border-slate-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowConfirmExtras((v) => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg"
                  data-testid="toggle-confirm-extras"
                >
                  <span>➕ Détails optionnels (source client, photo)</span>
                  <span className="text-slate-400">{showConfirmExtras ? "▲" : "▼"}</span>
                </button>

                {showConfirmExtras && (
                  <div className="px-3 pb-3 pt-1 space-y-3 border-t border-slate-100">
                    {/* Source d'acquisition : d'où vient le client ? */}
                    <div data-testid="source-acquisition-section">
                      <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2 block">Comment nous a-t-il connus ? (optionnel)</label>
                      <div className="grid grid-cols-3 sm:grid-cols-3 gap-1.5 sm:gap-2">
                        {SOURCES_ACQUISITION.map((src) => (
                          <button
                            key={src}
                            type="button"
                            onClick={() => setSourceAcquisition(sourceAcquisition === src ? "" : src)}
                            className={`px-1.5 py-1.5 sm:px-2 sm:py-2 rounded-lg text-[11px] leading-tight sm:text-xs font-medium border transition-colors ${sourceAcquisition === src ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                            data-testid={`source-btn-${src.toLowerCase().replace(/[^a-z]/g, '-')}`}
                          >
                            {src}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Photo de livraison */}
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2 block">Photo de livraison (optionnel) :</label>
                      {photoLivraison ? (
                        <div className="relative">
                          <img src={photoLivraison} alt="Photo livraison" className="w-full h-24 sm:h-32 object-cover rounded-lg border" />
                          <button
                            onClick={() => setPhotoLivraison(null)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center gap-2 h-12 sm:h-16 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                          <Camera className="w-5 h-5 text-slate-400" />
                          <span className="text-xs sm:text-sm text-slate-500">Prendre une photo</span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onloadend = () => setPhotoLivraison(reader.result);
                              reader.readAsDataURL(file);
                            }}
                            data-testid="photo-input"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Auto-envoi reçu email — visible seulement si client a un email */}
              {currentLivraison?.client_email && (
                <label className="flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg cursor-pointer" data-testid="auto-email-toggle-wrapper">
                  <input
                    type="checkbox"
                    checked={autoReceiptEmail}
                    onChange={(e) => setAutoReceiptEmail(e.target.checked)}
                    className="w-4 h-4 cursor-pointer"
                    data-testid="auto-email-toggle"
                  />
                  <span className="text-xs sm:text-sm text-indigo-800">
                    Envoyer automatiquement le reçu par email à <b>{currentLivraison.client_email}</b>
                  </span>
                </label>
              )}
            </div>

            <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-t bg-white flex-shrink-0" style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}>
              <Button
                size="lg"
                className="w-full h-12 sm:h-14 text-base sm:text-lg bg-green-600 hover:bg-green-700"
                onClick={handleConfirmDelivery}
                disabled={confirming}
                data-testid="confirm-delivery-btn"
              >
                {confirming ? (
                  <><span className="animate-spin mr-2">...</span>Confirmation...</>
                ) : (
                  <><CheckCircle className="w-5 h-5 mr-2" />CONFIRMER LA LIVRAISON</>
                )}
              </Button>
            </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent className="max-w-md mx-4" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Livraison confirmee !
            </DialogTitle>
          </DialogHeader>
          {lastDelivered && (
            <div className="space-y-4 mt-2">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <p className="font-bold text-green-800 text-lg">{lastDelivered.client_nom}</p>
                <p className="text-green-600">{lastDelivered.quantite}x {lastDelivered.details_commande || "articles"}</p>
                <p className="font-bold text-xl text-green-800 mt-2">
                  {lastDelivered.montant_encaisse != null ? lastDelivered.montant_encaisse : lastDelivered.prix || 0} EUR
                </p>
              </div>

              {/* Statuts d'envoi reçu (idempotence) */}
              <div className="grid grid-cols-2 gap-2" data-testid="receipt-status-badges">
                <div className={`p-2 rounded-lg border text-center text-xs ${lastDelivered.receipt_email_status === "sent" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : lastDelivered.receipt_email_status === "failed" ? "bg-red-50 border-red-200 text-red-800" : lastDelivered.receipt_email_status === "not_available" ? "bg-slate-50 border-slate-200 text-slate-500" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                  <Mail className="w-3.5 h-3.5 inline mr-1" />
                  {lastDelivered.receipt_email_status === "sent" ? "Email envoyé ✓" :
                    lastDelivered.receipt_email_status === "failed" ? "Email échoué" :
                    lastDelivered.receipt_email_status === "not_available" ? "Pas d'email" :
                    "Email — en attente"}
                </div>
                <div className={`p-2 rounded-lg border text-center text-xs ${lastDelivered.receipt_sms_status === "sent" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                  <Send className="w-3.5 h-3.5 inline mr-1" />
                  {lastDelivered.receipt_sms_status === "sent" ? "SMS envoyé ✓" : "SMS — non envoyé"}
                </div>
              </div>

              <Button size="lg" variant="outline" className="w-full h-12" onClick={() => downloadReceipt(lastDelivered.id)} data-testid="receipt-download-btn">
                <Download className="w-5 h-5 mr-2" />Telecharger le recu PDF
              </Button>

              {lastDelivered.client_telephone && (
                <Button size="lg" variant="outline" className="w-full h-12" onClick={() => sendReceiptSMS(lastDelivered)} data-testid="receipt-sms-btn">
                  <Send className="w-5 h-5 mr-2" />Envoyer le recu par SMS
                </Button>
              )}

              {lastDelivered.receipt_email_status === "failed" ? (
                <Button size="lg" className="w-full h-12 bg-red-600 hover:bg-red-700 text-white" onClick={() => resendReceiptEmailForce(lastDelivered.id)} disabled={resendingReceipt} data-testid="receipt-email-resend-btn">
                  <RefreshCw className={`w-5 h-5 mr-2 ${resendingReceipt ? "animate-spin" : ""}`} />
                  Renvoyer le reçu email
                </Button>
              ) : (
                <Button size="lg" variant="outline" className="w-full h-12 border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => sendReceiptEmail(lastDelivered)} data-testid="receipt-email-btn">
                  <Mail className="w-5 h-5 mr-2" />
                  {lastDelivered.receipt_email_status === "sent" ? "Renvoyer le reçu par Email" : "Envoyer le reçu par Email"}
                </Button>
              )}

              <Button
                size="lg"
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700"
                onClick={() => {
                  setReceiptDialogOpen(false);
                  if (currentIndex < totalStops - 1) setCurrentIndex(currentIndex + 1);
                }}
                data-testid="next-delivery-btn"
              >
                Livraison suivante <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-md mx-4" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-600" />
              Reporter la livraison
            </DialogTitle>
          </DialogHeader>
          {currentLivraison && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-bold text-slate-800">{currentLivraison.client_nom}</p>
                <p className="text-sm text-slate-500">{currentLivraison.adresse}, {currentLivraison.code_postal} {currentLivraison.ville}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Raison *</label>
                <Select value={reportReason} onValueChange={setReportReason}>
                  <SelectTrigger data-testid="report-reason-select">
                    <SelectValue placeholder="Choisir une raison..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Client absent">🚪 Client absent</SelectItem>
                    <SelectItem value="Client injoignable">📵 Client injoignable</SelectItem>
                    <SelectItem value="Client refuse la livraison">🙅 Client refuse la livraison</SelectItem>
                    <SelectItem value="Adresse incorrecte">📍 Adresse incorrecte</SelectItem>
                    <SelectItem value="Accès impossible (parking, portail…)">🚧 Accès impossible (parking, portail…)</SelectItem>
                    <SelectItem value="Étage / ascenseur inadapté">🏢 Étage / ascenseur inadapté</SelectItem>
                    <SelectItem value="Colis endommagé">📦 Colis endommagé</SelectItem>
                    <SelectItem value="Mauvais produit / SKU">🔀 Mauvais produit / SKU</SelectItem>
                    <SelectItem value="Report demandé par le client">📅 Report demandé par le client</SelectItem>
                    <SelectItem value="Problème véhicule">🚗 Problème véhicule</SelectItem>
                    <SelectItem value="Manque de temps / fin de tournée">⏰ Manque de temps / fin de tournée</SelectItem>
                    <SelectItem value="Météo / conditions dangereuses">🌧️ Météo / conditions dangereuses</SelectItem>
                    <SelectItem value="Autre">❓ Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Note complementaire</label>
                <Textarea
                  value={reportNote}
                  onChange={(e) => setReportNote(e.target.value)}
                  placeholder="Ex: Reportee au 28/02, client rappellera..."
                  rows={2}
                  className="bg-white"
                  data-testid="report-note-input"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={reportCreerProchaine} onChange={(e) => setReportCreerProchaine(e.target.checked)} className="w-5 h-5 mt-0.5 cursor-pointer" data-testid="report-creer-prochaine" />
                  <span>
                    <span className="font-semibold text-sm text-blue-800">Créer automatiquement la prochaine livraison</span>
                    <p className="text-xs text-blue-600 mt-0.5">Une nouvelle livraison (même client, mêmes articles) est créée en « En attente », prête à replanifier.</p>
                  </span>
                </label>
                {reportCreerProchaine && (
                  <div>
                    <label className="text-sm font-medium text-blue-700 mb-1 block">Date souhaitée (optionnel)</label>
                    <Input type="date" value={reportDateSouhaitee} onChange={(e) => setReportDateSouhaitee(e.target.value)} className="bg-white" data-testid="report-date-souhaitee" />
                  </div>
                )}
              </div>

              <Button
                size="lg"
                className="w-full h-14 text-lg bg-orange-600 hover:bg-orange-700"
                onClick={handleReport}
                disabled={reporting || !reportReason}
                data-testid="confirm-report-btn"
              >
                {reporting ? (
                  <><span className="animate-spin mr-2">...</span>Reporter...</>
                ) : (
                  <><RotateCcw className="w-5 h-5 mr-2" />CONFIRMER LE REPORT</>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <QrTabsModal open={showQrModal} onClose={() => setShowQrModal(false)} user={user} params={params} />

      {/* Floating "Frais" button — visible en permanence quand une tournée est active */}
      {selectedTournee && (
        <button
          type="button"
          onClick={() => { setFraisType("essence"); setFraisMontant(""); setFraisNote(""); setFraisDialogOpen(true); }}
          className="fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-xl flex items-center justify-center active:scale-95 transition-transform"
          style={{ bottom: "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))" }}
          data-testid="fab-frais-btn"
          title="Ajouter un frais (essence, péage…)"
        >
          <Fuel className="w-7 h-7" />
        </button>
      )}

      {/* Frais Dialog — optimisée mobile */}
      <Dialog open={fraisDialogOpen} onOpenChange={setFraisDialogOpen}>
        <DialogContent className="max-w-md mx-4" data-testid="livreur-frais-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Receipt className="w-5 h-5 text-amber-600" />
              Frais réel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">Type de frais</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: "essence", l: "Essence", e: "⛽" },
                  { v: "adblue", l: "AdBlue", e: "🔵" },
                  { v: "peage", l: "Péage", e: "🛣️" },
                  { v: "parking", l: "Parking", e: "🅿️" },
                  { v: "lavage", l: "Lavage", e: "🧽" },
                  { v: "autre", l: "Autre", e: "📌" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setFraisType(opt.v)}
                    className={`p-3 rounded-lg border-2 text-center transition-colors ${fraisType === opt.v ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`frais-type-${opt.v}`}
                  >
                    <div className="text-2xl mb-1">{opt.e}</div>
                    <div className="text-xs font-medium">{opt.l}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">Montant (€) *</label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                value={fraisMontant}
                onChange={(e) => setFraisMontant(e.target.value)}
                placeholder="100.00"
                className="h-14 text-2xl font-semibold text-center"
                autoFocus
                data-testid="frais-montant-input"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">Note (optionnel)</label>
              <Input
                value={fraisNote}
                onChange={(e) => setFraisNote(e.target.value)}
                placeholder="Station Total, ticket 1234…"
                className="h-11"
                data-testid="frais-note-input"
              />
            </div>

            <Button
              onClick={handleAddFrais}
              disabled={fraisSubmitting || !fraisMontant}
              className="w-full h-14 text-lg bg-amber-600 hover:bg-amber-700"
              data-testid="frais-submit-btn"
            >
              {fraisSubmitting ? "Enregistrement..." : "✓ Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Exchange Dialog ===== */}
      <Dialog open={exchangeDialogOpen} onOpenChange={setExchangeDialogOpen}>
        <DialogContent className="max-w-md mx-4 max-h-[92vh] flex flex-col p-0 gap-0" data-testid="exchange-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Repeat className="w-5 h-5 text-violet-600" />
              Enregistrer un échange
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
            {/* Chaises rapportées */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-slate-700">Chaises rapportées</label>
                <Button
                  type="button" size="sm" variant="outline"
                  className="h-7 text-xs border-violet-300 text-violet-700"
                  onClick={() => setExchangeRapportees([...exchangeRapportees, { produit_id: "", sku: "", modele: "", couleur: "", quantite: 1 }])}
                  data-testid="exchange-add-row"
                >
                  + Ajouter
                </Button>
              </div>
              {exchangeRapportees.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Clique « Ajouter » pour préciser les chaises que tu reprends.</p>
              ) : (
                <div className="space-y-2">
                  {exchangeRapportees.map((c, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`exchange-row-${idx}`}>
                      <div className="col-span-8">
                        <Select
                          value={c.produit_id || "none"}
                          onValueChange={(val) => {
                            const rows = [...exchangeRapportees];
                            if (val === "none") {
                              rows[idx] = { ...rows[idx], produit_id: "", sku: "", modele: "", couleur: "" };
                            } else {
                              const prod = produits.find(p => p.id === val);
                              if (prod) rows[idx] = { ...rows[idx], produit_id: prod.id, sku: prod.sku, modele: prod.modele, couleur: prod.couleur };
                            }
                            setExchangeRapportees(rows);
                          }}
                        >
                          <SelectTrigger className="h-10" data-testid={`exchange-prod-${idx}`}>
                            <SelectValue placeholder="Choisir…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-- Choisir le produit --</SelectItem>
                            {produits.map((p) => (
                              <SelectItem key={p.id} value={p.id}>{p.modele} {p.couleur}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number" min="1" inputMode="numeric"
                          value={c.quantite}
                          onChange={(e) => {
                            const rows = [...exchangeRapportees];
                            rows[idx] = { ...rows[idx], quantite: parseInt(e.target.value) || 1 };
                            setExchangeRapportees(rows);
                          }}
                          className="h-10 text-center"
                          data-testid={`exchange-qte-${idx}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setExchangeRapportees(exchangeRapportees.filter((_, i) => i !== idx))}
                        className="col-span-1 text-red-400 hover:text-red-600 text-xl"
                        data-testid={`exchange-rm-${idx}`}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Destination du stock */}
            <div className="bg-slate-50 rounded-lg p-3 border">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exchangeReintegrer}
                  onChange={(e) => setExchangeReintegrer(e.target.checked)}
                  className="w-5 h-5 mt-0.5 cursor-pointer"
                  data-testid="exchange-reintegrer"
                />
                <span>
                  <span className="font-semibold text-sm text-slate-700">Réintégrer au stock</span>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {exchangeReintegrer
                      ? "Les chaises rapportées sont remises en stock (réutilisables)"
                      : "⚠️ Les chaises seront marquées en perte (abîmées)"}
                  </p>
                </span>
              </label>
            </div>

            {/* Frais livraison + différence */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Frais livraison</label>
                <Input
                  type="number" inputMode="decimal" step="0.01"
                  value={exchangeFraisLivraison}
                  onChange={(e) => setExchangeFraisLivraison(e.target.value)}
                  className="h-12 text-lg font-semibold text-center"
                  data-testid="exchange-frais-input"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Différence (€)</label>
                <Input
                  type="number" inputMode="decimal" step="0.01"
                  value={exchangeDifference}
                  onChange={(e) => setExchangeDifference(e.target.value)}
                  className="h-12 text-lg text-center"
                  placeholder="0"
                  data-testid="exchange-diff-input"
                />
              </div>
            </div>

            {/* Récap montant */}
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 flex items-center justify-between">
              <span className="font-semibold text-violet-700">💰 Total à encaisser :</span>
              <span className="text-2xl font-bold text-violet-700" data-testid="exchange-total">
                {((parseFloat(exchangeFraisLivraison) || 0) + (parseFloat(exchangeDifference) || 0)).toFixed(2)} €
              </span>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Raison (optionnel)</label>
              <Input
                value={exchangeRaison}
                onChange={(e) => setExchangeRaison(e.target.value)}
                placeholder="Couleur ne convient pas, défaut, etc."
                className="h-11"
                data-testid="exchange-raison-input"
              />
            </div>
          </div>

          <div className="border-t bg-white px-5 py-3 shrink-0 flex gap-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0.75rem))" }}>
            <Button variant="outline" className="flex-1" onClick={() => setExchangeDialogOpen(false)}>Annuler</Button>
            <Button
              onClick={handleExchange}
              disabled={exchangeSubmitting || exchangeRapportees.length === 0}
              className="flex-1 bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="exchange-submit-btn"
            >
              {exchangeSubmitting ? "..." : <><Repeat className="w-4 h-4 mr-2" />Confirmer l'échange</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog : Modifier les articles (substitution sur place) */}
      <Dialog open={modifyDialogOpen} onOpenChange={setModifyDialogOpen}>
        <DialogContent className="max-w-md mx-4 max-h-[92vh] flex flex-col p-0 gap-0" data-testid="modify-articles-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Package className="w-5 h-5 text-amber-600" />
              Modifier les articles
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-0">
            <p className="text-xs text-slate-500">Remplace les articles que le client souhaite. Le total se recalcule selon le tarif. Le stock ne sera déduit qu'à la validation « LIVRÉ ».</p>
            {modifyItems.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`modify-row-${idx}`}>
                <div className="col-span-6">
                  <Select
                    value={it.produit_id || "none"}
                    onValueChange={(val) => {
                      const rows = [...modifyItems];
                      if (val === "none") {
                        rows[idx] = { ...rows[idx], produit_id: "", sku: "", modele: "", couleur: "" };
                      } else {
                        const prod = produits.find((p) => p.id === val);
                        if (prod) rows[idx] = { ...rows[idx], produit_id: prod.id, sku: prod.sku, modele: prod.modele, couleur: prod.couleur, prix_unitaire: rows[idx].prix_unitaire || prod.prix_b2c_ttc || 0 };
                      }
                      setModifyItems(rows);
                    }}
                  >
                    <SelectTrigger className="h-10" data-testid={`modify-prod-${idx}`}><SelectValue placeholder="Modèle…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Choisir --</SelectItem>
                      {produits.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.modele} {p.couleur} (stock {p.stock_restant})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Input type="number" min="1" inputMode="numeric" value={it.quantite}
                    onChange={(e) => { const rows = [...modifyItems]; rows[idx] = { ...rows[idx], quantite: parseInt(e.target.value) || 1 }; setModifyItems(rows); }}
                    className="h-10 text-center" title="Quantité" data-testid={`modify-qte-${idx}`} />
                </div>
                <div className="col-span-3">
                  <Input type="number" step="0.01" min="0" value={it.prix_unitaire}
                    onChange={(e) => { const rows = [...modifyItems]; rows[idx] = { ...rows[idx], prix_unitaire: e.target.value === "" ? "" : parseFloat(e.target.value) }; setModifyItems(rows); }}
                    className="h-10 text-center" placeholder="Prix €" title="Prix unitaire" data-testid={`modify-prix-${idx}`} />
                </div>
                <button type="button" onClick={() => setModifyItems(modifyItems.length > 1 ? modifyItems.filter((_, i) => i !== idx) : modifyItems)}
                  className="col-span-1 text-red-400 hover:text-red-600 text-xl" data-testid={`modify-rm-${idx}`}>×</button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-amber-300 text-amber-700"
              onClick={() => setModifyItems([...modifyItems, { produit_id: "", sku: "", modele: "", couleur: "", quantite: 1, prix_unitaire: 0 }])}
              data-testid="modify-add-row">+ Ajouter un article</Button>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between mt-2">
              <span className="font-semibold text-amber-700">Nouveau total {(currentLivraison?.frais_livraison || 0) > 0 ? "(frais inclus)" : ""} :</span>
              <span className="text-2xl font-bold text-amber-700" data-testid="modify-total">
                {(modifyItems.reduce((a, it) => a + (parseFloat(it.prix_unitaire) || 0) * (parseInt(it.quantite) || 1), 0) + (currentLivraison?.frais_livraison || 0)).toFixed(2)} €
              </span>
            </div>
          </div>

          <div className="border-t bg-white px-5 py-3 shrink-0 flex gap-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0.75rem))" }}>
            <Button variant="outline" className="flex-1" onClick={() => setModifyDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleModifyArticles} disabled={modifySubmitting} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="modify-submit-btn">
              {modifySubmitting ? "..." : <><Package className="w-4 h-4 mr-2" />Enregistrer</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Km au démarrage tournée */}
      <Dialog open={kmDialogOpen} onOpenChange={setKmDialogOpen}>
        <DialogContent className="max-w-md mx-4" data-testid="km-start-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">🚗 Carnet de route — Départ</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2">
              Enregistre le kilométrage + une photo du compteur avant de démarrer la tournée.
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Véhicule</label>
              <Select
                value={kmForm.vehicule_id}
                onValueChange={(val) => {
                  const v = vehicules.find(x => x.id === val);
                  setKmForm({ ...kmForm, vehicule_id: val, km_depart: v?.kilometrage || kmForm.km_depart });
                }}
              >
                <SelectTrigger data-testid="km-vehicule-select">
                  <SelectValue placeholder="— Sélectionner un véhicule —" />
                </SelectTrigger>
                <SelectContent>
                  {vehicules.length === 0 ? (
                    <div className="p-3 text-sm text-slate-400">Aucun véhicule enregistré</div>
                  ) : (
                    vehicules.map(v => (
                      <SelectItem key={v.id} value={v.id} data-testid={`km-vehicule-option-${v.id}`}>
                        {[v.nom, v.marque, v.modele, v.plaque || v.immatriculation].filter(Boolean).join(" ").trim() || `Véhicule ${v.id.slice(0, 6)}`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Kilométrage actuel</label>
              <Input
                type="number"
                value={kmForm.km_depart}
                onChange={(e) => setKmForm({ ...kmForm, km_depart: e.target.value })}
                placeholder="Ex: 125 400"
                data-testid="km-depart-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Photo du compteur *</label>
              {kmForm.photo_depart ? (
                <div className="flex items-center gap-2">
                  <img src={`${API}/files/${kmForm.photo_depart}?auth=${token}`} alt="" className="h-16 w-24 object-cover rounded border" />
                  <Button size="sm" variant="ghost" onClick={() => setKmForm({ ...kmForm, photo_depart: "" })}>Changer</Button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 h-14 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50">
                  <Camera className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">Prendre la photo</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadKmPhoto(e.target.files?.[0])} data-testid="km-photo-input" />
                </label>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={handleSkipKm} disabled={kmSubmitting} data-testid="km-skip-btn">
                Ignorer
              </Button>
              <Button
                onClick={handleSaveKmAndStart}
                disabled={kmSubmitting}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="km-save-start-btn"
              >
                {kmSubmitting ? "..." : "Enregistrer & Démarrer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Fin de tournée */}
      <Dialog open={finTourneeOpen} onOpenChange={setFinTourneeOpen}>
        <DialogContent className="max-w-md mx-4" data-testid="fin-tournee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle className="w-5 h-5" /> Terminer la tournée
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {/* Récap tournée */}
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="fin-tournee-recap">
              <div className="grid grid-cols-3 gap-2 text-center mb-2">
                <div>
                  <p className="text-2xl font-bold text-emerald-800" data-testid="recap-livrees">{tourneeSummary.livreesCount}</p>
                  <p className="text-[10px] uppercase tracking-wide text-emerald-600">Livrées</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-700" data-testid="recap-reportees">{tourneeSummary.reporteesCount}</p>
                  <p className="text-[10px] uppercase tracking-wide text-orange-600">Reportées</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-700" data-testid="recap-annulees">{tourneeSummary.annuleesCount}</p>
                  <p className="text-[10px] uppercase tracking-wide text-red-600">Annulées</p>
                </div>
              </div>
              <div className="border-t border-emerald-200 pt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">💵 Cash</span>
                  <span className="font-semibold text-emerald-800" data-testid="recap-cash">{tourneeSummary.cash.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">🏦 Virement</span>
                  <span className="font-semibold text-blue-800" data-testid="recap-virement">{tourneeSummary.virement.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between border-t border-emerald-200 pt-1 mt-1">
                  <span className="text-slate-700 font-bold">Total encaissé</span>
                  <span className="font-bold text-emerald-900" data-testid="recap-total">{tourneeSummary.total.toFixed(2)} €</span>
                </div>
              </div>
            </div>

            <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg p-2">
              Enregistre le kilométrage final + une photo (camion, compteur ou autre) pour clôturer le carnet.
            </div>
            {openTripInfo?.km_depart != null && (
              <div className="text-xs text-slate-500">
                Km départ enregistré : <strong>{openTripInfo.km_depart}</strong>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1 block">Kilométrage final *</label>
              <Input
                type="number"
                value={finTourneeForm.km_retour}
                onChange={(e) => setFinTourneeForm({ ...finTourneeForm, km_retour: e.target.value })}
                placeholder="Ex: 125 500"
                data-testid="fin-km-retour-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Photo *</label>
              {finTourneeForm.photo_retour ? (
                <div className="flex items-center gap-2">
                  <img src={`${API}/files/${finTourneeForm.photo_retour}?auth=${token}`} alt="" className="h-16 w-24 object-cover rounded border" />
                  <Button size="sm" variant="ghost" onClick={() => setFinTourneeForm({ ...finTourneeForm, photo_retour: "" })}>Changer</Button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 h-14 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50">
                  <Camera className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">Prendre la photo</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadFinTourneePhoto(e.target.files?.[0])} data-testid="fin-photo-input" />
                </label>
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Remarques (optionnel)</label>
              <Input
                value={finTourneeForm.remarques}
                onChange={(e) => setFinTourneeForm({ ...finTourneeForm, remarques: e.target.value })}
                placeholder="Ex: RAS / crevaison / panne…"
                data-testid="fin-remarques-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setFinTourneeOpen(false)} disabled={finTourneeSubmitting}>
                Annuler
              </Button>
              <Button
                onClick={handleSubmitFinTournee}
                disabled={finTourneeSubmitting}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="fin-tournee-submit-btn"
              >
                {finTourneeSubmitting ? "..." : "Clôturer la tournée"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifier le prix (remises séparées Chaises / Livraison) */}
      <Dialog open={modifyPrixOpen} onOpenChange={setModifyPrixOpen}>
        <DialogContent className="max-w-md mx-4" data-testid="modify-prix-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">💸 Modifier le prix</DialogTitle>
          </DialogHeader>
          {(() => {
            const pChaise = Number(modifyPrixForm.prix_chaise) || 0;
            const fraisLiv = Number(modifyPrixForm.frais_livraison) || 0;
            const totalChaises = pChaise * modifyPrixInitial.total_chaises;
            const nouveauTotal = totalChaises + fraisLiv;
            const remiseChaises = Math.max(0, (modifyPrixInitial.prix_chaise - pChaise) * modifyPrixInitial.total_chaises);
            const remiseLivraison = Math.max(0, modifyPrixInitial.frais_livraison - fraisLiv);
            const remiseTotale = remiseChaises + remiseLivraison;
            return (
              <div className="space-y-4 mt-2">
                <div className="text-xs bg-slate-100 rounded-lg p-2 text-slate-600">
                  {modifyPrixInitial.total_chaises} chaise(s) · prix catalogue {modifyPrixInitial.prix_chaise.toFixed(2)}€ · livraison {modifyPrixInitial.frais_livraison.toFixed(2)}€
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">🪑 Prix chaise (unitaire)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={modifyPrixForm.prix_chaise}
                    onChange={(e) => setModifyPrixForm({ ...modifyPrixForm, prix_chaise: e.target.value })}
                    data-testid="modify-prix-chaise-input"
                  />
                  {remiseChaises > 0 && <div className="text-xs text-rose-700 mt-1">💸 Remise chaises : -{remiseChaises.toFixed(2)} €</div>}
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">🚚 Frais de livraison</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={modifyPrixForm.frais_livraison}
                    onChange={(e) => setModifyPrixForm({ ...modifyPrixForm, frais_livraison: e.target.value })}
                    data-testid="modify-prix-frais-input"
                  />
                  {remiseLivraison > 0 && <div className="text-xs text-rose-700 mt-1">💸 Remise livraison : -{remiseLivraison.toFixed(2)} €</div>}
                </div>
                <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                  <div className="flex justify-between text-sm text-slate-600"><span>Total chaises</span><span>{totalChaises.toFixed(2)} €</span></div>
                  <div className="flex justify-between text-sm text-slate-600"><span>+ Livraison</span><span>{fraisLiv.toFixed(2)} €</span></div>
                  <div className="flex justify-between text-base font-bold text-indigo-800 mt-1 pt-1 border-t border-indigo-200"><span>Nouveau total</span><span>{nouveauTotal.toFixed(2)} €</span></div>
                  {remiseTotale > 0 && <div className="flex justify-between text-sm text-rose-700 font-medium mt-1"><span>Remise totale</span><span>-{remiseTotale.toFixed(2)} €</span></div>}
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Raison (obligatoire)</label>
                  <Input
                    placeholder="ex: remise cliente fidèle, offert par patron…"
                    value={modifyPrixForm.raison}
                    onChange={(e) => setModifyPrixForm({ ...modifyPrixForm, raison: e.target.value })}
                    data-testid="modify-prix-raison-input"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setModifyPrixOpen(false)} disabled={savingPrix}>Annuler</Button>
                  <Button onClick={saveModifyPrix} disabled={savingPrix} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="modify-prix-save-btn">
                    {savingPrix ? "..." : "Enregistrer"}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Livreur;
